import { ActionIcon, Anchor, Badge, Card, Collapse, Group, Stack, Table, Text, UnstyledButton } from '@mantine/core';
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import { Fragment, useState } from 'react';
import { OpResultBadge } from '../StatusBadge/StatusBadge.js';
import { labelOf } from '../../domain/labels.js';
import { toggled } from '../../domain/toggled.js';
import { TypeIcon } from '../TypeGlyph/TypeIcon.js';
import { useIsMobile } from '../../hooks/useIsMobile.js';
import { RecordLabel } from '../Diff/RecordLabel.js';
import { PushOpDetail } from './PushOpDetail.js';
import { opFieldsText, opKindOf } from './pushSummary.js';
import { OP_RESULT, type OpResult } from '../../domain/status.js';

export type PushOpRow = {
    seq: number;
    op: 'create' | 'update';
    /** Set on an update that moves a game from this key to `entityKey`. */
    fromKey?: string | null;
    entityType: string;
    entityKey: string;
    expectedHash: string | null;
    patch: Record<string, unknown>;
    /** The desired record's name; null when it has none. */
    title: string | null;
    /** An update's environment values before it, for the fields it writes. Null for a create and for older ops. */
    before: Record<string, unknown> | null;
    result: OpResult | null;
    resultHash: string | null;
    resultReason: string | null;
};

/**
 * A plan's operations, in applied order.
 *
 * Never desktop-only: at this column count a horizontally scrolling table is unusable on a
 * phone, and a plan is exactly the thing someone checks from one.
 *
 * A row names the record and, in plain text, the fields it writes (or `Whole record`). Each
 * row expands in place to `PushOpDetail` — the fields it writes, before and after, or the whole
 * record for a create — rather than sending someone to the record to find out what a plan is
 * actually going to write.
 */
export const PushOps = ({ ops }: { ops: PushOpRow[] }) => {
    const [expanded, setExpanded] = useState<Set<number>>(new Set());

    const toggle = (seq: number) => setExpanded((prev) => toggled(prev, seq));

    const allExpanded = ops.length > 0 && ops.every((op) => expanded.has(op.seq));
    const toggleAll = () => setExpanded(allExpanded ? new Set() : new Set(ops.map((op) => op.seq)));

    return (
        <Stack gap="xs">
            {ops.length > 1 && (
                <Anchor
                    component="button"
                    type="button"
                    fz="xs"
                    onClick={toggleAll}
                    style={{ alignSelf: 'flex-start' }}
                >
                    {allExpanded ? 'Collapse all' : 'Expand all'}
                </Anchor>
            )}
            {useIsMobile() ? (
                <PushOpCards ops={ops} expanded={expanded} toggle={toggle} />
            ) : (
                <PushOpTable ops={ops} expanded={expanded} toggle={toggle} />
            )}
        </Stack>
    );
};

type SharedProps = {
    ops: PushOpRow[];
    expanded: Set<number>;
    toggle: (seq: number) => void;
};

const PushOpCards = ({ ops, expanded, toggle }: SharedProps) => (
    <Stack gap="xs">
        {ops.map((op) => {
            const isExpanded = expanded.has(op.seq);
            return (
                <Card key={op.seq} p="sm">
                    <UnstyledButton
                        onClick={() => toggle(op.seq)}
                        aria-expanded={isExpanded}
                        aria-label={isExpanded ? `Collapse operation ${op.seq}` : `Expand operation ${op.seq}`}
                        style={{ width: '100%', textAlign: 'left' }}
                    >
                        <Group justify="space-between" wrap="nowrap" mb={4}>
                            <Group gap={4} wrap="nowrap">
                                {isExpanded ? (
                                    <IconChevronDown size={16} stroke={1.7} />
                                ) : (
                                    <IconChevronRight size={16} stroke={1.7} />
                                )}
                                <RecordLabel title={op.title} entityKey={op.entityKey} />
                            </Group>
                            <OpResultBadge result={op.result ?? 'pending'} size="xs" />
                        </Group>
                        <Group gap={6} wrap="wrap">
                            <Badge size="xs" variant="light" color="gray" radius="sm" tt="none">
                                {opKindOf(op).badge}
                            </Badge>
                            <Group gap={4} wrap="nowrap">
                                <TypeIcon type={op.entityType} size={12} stroke={1.7} />
                                <Text fz="xs" c="dimmed">
                                    {labelOf(op.entityType).singular}
                                </Text>
                            </Group>
                            <MovedFrom op={op} fz="xs" />
                        </Group>
                        {!isExpanded && <OpDetailLine op={op} mt={4} />}
                    </UnstyledButton>
                    <Collapse in={isExpanded}>
                        <PushOpDetail op={op} />
                    </Collapse>
                </Card>
            );
        })}
    </Stack>
);

const PushOpTable = ({ ops, expanded, toggle }: SharedProps) => (
    <Card p={0} style={{ overflow: 'hidden' }}>
        <Table verticalSpacing={6} horizontalSpacing="sm" highlightOnHover>
            <Table.Thead style={{ background: 'var(--rgs-surface-2)' }}>
                <Table.Tr>
                    <Table.Th w={36} />
                    <Table.Th w={50}>#</Table.Th>
                    <Table.Th w={80}>Op</Table.Th>
                    <Table.Th>Record</Table.Th>
                    <Table.Th w={130}>Result</Table.Th>
                    <Table.Th>Detail</Table.Th>
                </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
                {ops.map((op) => {
                    const isExpanded = expanded.has(op.seq);
                    return (
                        <Fragment key={op.seq}>
                            <Table.Tr>
                                <Table.Td>
                                    <ActionIcon
                                        variant="subtle"
                                        color="gray"
                                        size="sm"
                                        aria-label={
                                            isExpanded ? `Collapse operation ${op.seq}` : `Expand operation ${op.seq}`
                                        }
                                        aria-expanded={isExpanded}
                                        onClick={() => toggle(op.seq)}
                                    >
                                        {isExpanded ? (
                                            <IconChevronDown size={16} stroke={1.7} />
                                        ) : (
                                            <IconChevronRight size={16} stroke={1.7} />
                                        )}
                                    </ActionIcon>
                                </Table.Td>
                                <Table.Td>
                                    <Text className="rgs-numeric" fz="xs">
                                        {op.seq}
                                    </Text>
                                </Table.Td>
                                <Table.Td>
                                    <Badge size="xs" variant="light" color="gray" radius="sm" tt="none">
                                        {opKindOf(op).badge}
                                    </Badge>
                                </Table.Td>
                                <Table.Td onClick={() => toggle(op.seq)} style={{ cursor: 'pointer' }}>
                                    <RecordLabel title={op.title} entityKey={op.entityKey} />
                                    <Group gap={4} wrap="nowrap">
                                        <TypeIcon type={op.entityType} size={12} stroke={1.7} />
                                        <Text fz={10} c="dimmed">
                                            {labelOf(op.entityType).singular}
                                        </Text>
                                        <MovedFrom op={op} fz={10} />
                                    </Group>
                                </Table.Td>
                                <Table.Td>
                                    <OpResultBadge result={op.result ?? 'pending'} size="xs" />
                                </Table.Td>
                                <Table.Td>
                                    {/* Expanded, the detail below carries the reason; say it once. */}
                                    <OpDetailLine op={op} showReason={!isExpanded} />
                                </Table.Td>
                            </Table.Tr>
                            {isExpanded && (
                                <Table.Tr>
                                    <Table.Td colSpan={6} style={{ background: 'var(--rgs-surface-2)' }}>
                                        <PushOpDetail op={op} />
                                    </Table.Td>
                                </Table.Tr>
                            )}
                        </Fragment>
                    );
                })}
            </Table.Tbody>
        </Table>
    </Card>
);

/** The fields an operation writes, in plain text, and a failure's or conflict's reason beneath. */
const OpDetailLine = ({ op, mt, showReason = true }: { op: PushOpRow; mt?: number; showReason?: boolean }) => (
    <Stack gap={0} mt={mt}>
        <Text fz="xs" c="dimmed">
            {opFieldsText(op)}
        </Text>
        {showReason && op.resultReason && (
            <Text fz="xs" c={OP_RESULT[op.result ?? 'pending'].color} lineClamp={1} title={op.resultReason}>
                {op.resultReason}
            </Text>
        )}
    </Stack>
);

/** A move's `Move from <parent>`, dimmed beside the record's type; nothing for any other op. */
const MovedFrom = ({ op, fz }: { op: PushOpRow; fz: 'xs' | number }) => {
    const { movedFrom } = opKindOf(op);
    return movedFrom ? (
        <Text fz={fz} c="dimmed">
            {movedFrom}
        </Text>
    ) : null;
};
